import React, { useEffect, useMemo, useRef, useState } from "react";
import { env } from "../../../config/env";
import {
  Search,
  ChevronDown,
  Pencil,
  Trash2,
  Power,
  UserCheck,
  UserX,
  Plus,
  X,
  CheckCircle2,
  XCircle,
  Eye,
} from "lucide-react";
import { DeliveryPartnersService } from "../../../services/deliveryPartners.service";
import ConfirmModal from "../components/ConfirmModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { locationService } from "../../../services/location.service";


const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";
const BORDER = "#f2c6cc";



function resolveAssetUrl(url) {
  const value = String(url || "").trim();
  if (!value) return "";
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }
  return `${env.AUTH_BASE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-[18px] bg-white ${className}`}
      style={{ border: `1px solid ${BORDER}` }}
    >
      {children}
    </div>
  );
}

function StatCard({ title, value, tone = "pink" }) {
  const tones = {
    pink: {
      bg: "#fff8f9",
      border: "#f2c6cc",
      title: "#d86a78",
      value: "#d86a78",
    },
    yellow: {
      bg: "#fffaf0",
      border: "#f1dfb8",
      title: "#c58b13",
      value: "#b7791f",
    },
    green: {
      bg: "#f6fff7",
      border: "#cfe7d1",
      title: "#69a65f",
      value: "#5b9a50",
    },
    red: {
      bg: "#fff8f8",
      border: "#f0d4d4",
      title: "#d86a78",
      value: "#d86a78",
    },
  };

  const c = tones[tone] || tones.pink;



  return (
    <div
      className="rounded-[18px] px-4 py-4 sm:px-5"
      style={{ background: c.bg, border: `1px solid ${c.border}` }}
    >
      <div
        className="text-[12px] sm:text-[13px] font-semibold uppercase tracking-wide"
        style={{ color: c.title }}
      >
        {title}
      </div>
      <div
        className="mt-1 text-[34px] sm:text-[42px] font-bold leading-none"
        style={{ color: c.value }}
      >
        {value}
      </div>
    </div>
  );
}

function Select({ value, onChange, children, name }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const options = React.Children.toArray(children)
    .filter((child) => React.isValidElement(child))
    .map((child) => ({
      value: String(child.props.value ?? ""),
      label: child.props.children,
      disabled: Boolean(child.props.disabled),
    }));

  const selected =
    options.find((option) => String(option.value) === String(value)) ||
    options[0] ||
    { value: "", label: "Select" };

  useEffect(() => {
    const handleOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleSelect = (option) => {
    if (option.disabled) return;

    onChange?.({
      target: {
        name,
        value: option.value,
      },
    });

    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex h-[44px] w-full items-center justify-between rounded-[12px] border bg-white px-4 text-left text-[14px] font-medium outline-none transition ${
          open
            ? "border-[#d86a78] ring-2 ring-[#f7d7db]"
            : "border-[#f2c6cc] text-slate-600 hover:border-[#d86a78]"
        }`}
      >
        <span className="min-w-0 flex-1 truncate text-slate-600">
          {selected?.label}
        </span>

        <ChevronDown
          size={16}
          className={`ml-3 shrink-0 text-slate-400 transition ${
            open ? "rotate-180 text-[#d86a78]" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-[50px] z-[220] max-h-[260px] overflow-y-auto rounded-[14px] border border-[#f2c6cc] bg-white p-1 shadow-[0_18px_45px_rgba(15,23,42,0.16)]">
          {options.map((option, index) => {
            const active = String(option.value) === String(value);

            return (
              <button
                key={`${name || "select"}-${option.value}-${index}`}
                type="button"
                disabled={option.disabled}
                onClick={() => handleSelect(option)}
                className={`block w-full rounded-[10px] px-3 py-2.5 text-left text-[13px] transition ${
                  option.disabled
                    ? "cursor-not-allowed text-slate-300"
                    : active
                      ? "bg-[#fff1f3] font-semibold text-[#d86a78]"
                      : "text-slate-700 hover:bg-[#fff7f8] hover:text-[#d86a78]"
                }`}
              >
                <span className="block truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function Badge({ children, type = "default" }) {
  const styles = {
    active: { bg: "#eef9ee", color: "#69a65f" },
    inactive: { bg: "#f8f1f1", color: "#a66a6a" },
    approved: { bg: "#eef9ee", color: "#69a65f" },
    pending: { bg: "#fff8e7", color: "#b7791f" },
    rejected: { bg: "#fff1f1", color: "#d86a78" },
    default: { bg: "#f4f6f8", color: "#64748b" },
  };

  const s = styles[type] || styles.default;

  return (
    <span
      className="inline-flex min-h-[28px] items-center rounded-full px-3 text-[12px] font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}
    >
      {children}
    </span>
  );
}

function ApprovalPill({ value }) {
  const normalized = String(value || "").toUpperCase();

  const type =
    normalized === "APPROVED"
      ? "approved"
      : normalized === "REJECTED"
        ? "rejected"
        : normalized === "PENDING"
          ? "pending"
          : "default";

  const label =
    normalized === "APPROVED"
      ? "Approved"
      : normalized === "REJECTED"
        ? "Rejected"
        : normalized === "PENDING"
          ? "Pending"
          : "Not Submitted";

  return <Badge type={type}>{label}</Badge>;
}

function LiveStatusPill({ status, activeTaskCount = 0 }) {
  const normalized = String(status || "OFFLINE").toUpperCase();
  const config = {
    AVAILABLE: { label: "Online", type: "active" },
    BUSY: { label: activeTaskCount > 0 ? `Busy (${activeTaskCount})` : "Busy", type: "pending" },
    BUSY_OFFLINE: { label: activeTaskCount > 0 ? `Busy · Offline (${activeTaskCount})` : "Busy · Offline", type: "rejected" },
    PAUSED: { label: "Online · Paused", type: "default" },
    OFFLINE: { label: "Offline", type: "inactive" },
  };
  const current = config[normalized] || config.OFFLINE;
  return <Badge type={current.type}>{current.label}</Badge>;
}

function ModalInput({ label, name, value, onChange, placeholder = "", type = "text", disabled = false, maxLength, autoComplete }) {
  return (
    <label className="block">
      <div className="mb-2 text-[13px] font-semibold text-slate-600">
        {label}
      </div>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        autoComplete={autoComplete}
        className="h-[46px] w-full rounded-[12px] bg-white px-4 text-[14px] text-slate-700 outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
        style={{ border: `1px solid ${BORDER}` }}
      />
    </label>
  );
}

function DeliveryPartnerModal({
  open,
  mode,
  form,
  onChange,
  onClose,
  onSubmit,
  saving,
  avatarFile,
  avatarPreview,
  onAvatarChange,
  cityOptions = [],
  cityLoading = false,
  showCityDropdown = false,
  setShowCityDropdown = () => { },
  filteredCityOptions = [],
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 p-3 sm:p-4">
      <div className="max-h-[92vh] w-full max-w-[760px] overflow-y-auto rounded-[24px] bg-white shadow-2xl">
        <div
          className="flex items-start justify-between gap-4 border-b px-4 py-4 sm:px-6 sm:py-5"
          style={{ borderColor: BORDER }}
        >
          <div>
            <h2 className="text-[20px] sm:text-[24px] font-bold text-[#d86a78]">
              {mode === "edit"
                ? "Edit Delivery Partner"
                : "Add New Delivery Partner"}
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              {mode === "edit"
                ? "Update delivery partner details."
                : "Create a new delivery partner."}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-[#d86a78]"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} noValidate className="px-4 py-5 sm:px-6 sm:py-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ModalInput
              label="Full Name"
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Enter full name"
            />

            <ModalInput
              label="Email"
              name="email"
              type="text"
              value={form.email}
              onChange={onChange}
              placeholder="Enter email"
              disabled={mode === "edit"}
            />

            <ModalInput
              label="Phone"
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder="Enter phone number"
              maxLength={10}
              autoComplete="off"
            />

            <ModalInput
              label="Vehicle Type"
              name="vehicleType"
              value={form.vehicleType}
              onChange={onChange}
              placeholder="Bike / Scooter / Van"
            />

            <ModalInput
              label="Pincode"
              name="pincode"
              value={form.pincode}
              onChange={onChange}
              placeholder="Enter pincode"
              type="text"
              maxLength={6}
              autoComplete="off"
            />

            <ModalInput
              label="State"
              name="state"
              value={form.state}
              onChange={onChange}
              placeholder="Enter state"
            />

            <div className="relative">
              <div className="mb-2 text-[13px] font-semibold text-slate-600">City</div>

              <div className="relative">
                <input
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={(e) => {
                    onChange(e);
                    setShowCityDropdown(true);
                  }}
                  onFocus={() => setShowCityDropdown(true)}
                  onBlur={() => {
                    setTimeout(() => setShowCityDropdown(false), 200);
                  }}
                  placeholder={cityLoading ? "Loading cities..." : "Enter or select city"}
                  autoComplete="off"
                  className="h-[46px] w-full rounded-[12px] bg-white px-4 pr-10 text-[14px] text-slate-700 outline-none"
                  style={{ border: `1px solid ${BORDER}` }}
                />

                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setShowCityDropdown((prev) => !prev);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  <ChevronDown size={16} />
                </button>
              </div>

              {showCityDropdown ? (
                <div
                  className="absolute left-0 right-0 top-[74px] z-[120] max-h-56 overflow-auto rounded-[12px] border bg-white shadow-lg"
                  style={{ borderColor: BORDER }}
                >
                  {cityLoading ? (
                    <div className="px-4 py-3 text-[13px] text-slate-400">
                      Loading cities...
                    </div>
                  ) : filteredCityOptions.length > 0 ? (
                    filteredCityOptions.map((city) => (
                      <button
                        key={city}
                        type="button"
                        className="block w-full px-4 py-3 text-left text-[14px] text-slate-700 hover:bg-rose-50"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          onChange({
                            target: { name: "city", value: city },
                          });
                          setShowCityDropdown(false);
                        }}
                      >
                        {city}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-[13px] text-slate-400">
                      No city found
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="md:col-span-2">
              <ModalInput
                label="Full Address"
                name="fullAddress"
                value={form.fullAddress}
                onChange={onChange}
                placeholder="Enter full address"
              />
            </div>

            <ModalInput
              label="Vehicle Number"
              name="vehicleNumber"
              value={form.vehicleNumber}
              onChange={onChange}
              placeholder="Enter vehicle number"
            />

            <ModalInput
              label="Hub"
              name="hub"
              value={form.hub}
              onChange={onChange}
              placeholder="Enter hub"
            />

            <div className="md:col-span-2">
              <div className="mb-2 text-[13px] font-semibold text-slate-600">
                Profile Picture
              </div>

              <div
                className="flex flex-col gap-4 rounded-[14px] border p-4 sm:flex-row sm:items-center"
                style={{ borderColor: BORDER }}
              >
                <div
                  className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-slate-50"
                  style={{ borderColor: BORDER }}
                >
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="avatar preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-xs text-slate-400">No Image</span>
                  )}
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onAvatarChange}
                    className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-rose-50 file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-[#d86a78] hover:file:bg-rose-100"
                  />

                  <div className="text-xs text-slate-500">
                    {avatarFile ? "Image selected" : "Choose image"}
                  </div>
                </div>
              </div>
            </div>

            {mode === "edit" && (
              <>
                <div>
                  <div className="mb-2 text-[13px] font-semibold text-slate-600">
                    Status
                  </div>
                  <Select name="status" value={form.status} onChange={onChange}>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </Select>
                </div>

                {/* <div>
                  <div className="mb-2 text-[13px] font-semibold text-slate-600">
                    Approval Status
                  </div>
                  <Select
                    name="approvalStatus"
                    value={form.approvalStatus}
                    onChange={onChange}
                  >
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </Select>
                </div> */}
              </>
            )}
          </div>

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-[46px] w-full sm:w-auto items-center justify-center rounded-full px-6 text-[14px] font-semibold text-slate-600 transition hover:bg-slate-50"
              style={{ border: `1px solid ${BORDER}` }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex h-[46px] w-full sm:w-auto items-center justify-center rounded-full px-6 text-[14px] font-semibold text-white transition disabled:opacity-60"
              style={{ backgroundColor: PINK }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = PINK_HOVER)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = PINK)
              }
            >
              {saving
                ? mode === "edit"
                  ? "Updating..."
                  : "Creating..."
                : mode === "edit"
                  ? "Update Partner"
                  : "Create Partner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function normalizePartner(row = {}) {
  const statusRaw = String(
    row.status ??
    row.partnerStatus ??
    row.accountStatus ??
    (row.isActive ? "ACTIVE" : "INACTIVE")
  ).toUpperCase();

  const approvalSource = String(
    row.approvalStatus ??
    row.approval_status ??
    row.onboardingStatus ??
    row.approval ??
    ""
  )
    .trim()
    .toUpperCase();

  let approvalRaw = "NOT_SUBMITTED";

  if (approvalSource === "APPROVED") {
    approvalRaw = "APPROVED";
  } else if (approvalSource === "REJECTED") {
    approvalRaw = "REJECTED";
  } else if (approvalSource === "PENDING") {
    approvalRaw = "PENDING";
  } else {
    approvalRaw = "NOT_SUBMITTED";
  }

  return {
    id: row.id || row._id || "",
    partnerId: row.partnerId || row.partner_id || "",
    name: row.name || row.fullName || row.partnerName || "-",
    email: row.email || "-",
    phone: row.phone || row.mobile || "-",
    avatar: resolveAssetUrl(row.avatar || ""),
    vehicleType: row.vehicleType || row.vehicle_type || row.vehicle || "-",
    vehicleNumber: row.vehicleNumber || row.vehicle_number || "-",
    city: row.city || row.location?.city || row.zone || "-",
    hub: row.hub || "-",
    status: statusRaw,
    approvalStatus: approvalRaw,
    onboarding: row.onboarding ?? row.isOnboarded ?? false,
    onboardingStatus: row.onboardingStatus || row.onboarding_status || "",
    joinedAt: row.createdAt || row.created_at || row.joinedAt || "",
    liveStatus: String(row.liveStatus || row.live_status || "OFFLINE").toUpperCase(),
    isOnline: Boolean(row.isOnline ?? row.is_online ?? false),
    isAvailable: Boolean(row.isAvailable ?? row.is_available ?? false),
    isFresh: Boolean(row.isFresh ?? row.is_fresh ?? false),
    activeTaskCount: Number(row.activeTaskCount ?? row.active_task_count ?? 0),
    currentTaskId: row.currentTaskId || row.current_task_id || null,
    lastSeenAt: row.lastSeenAt || row.last_seen_at || null,
    raw: row,
  };
}

function getRowsFromResponse(res) {
  const list =
    res?.items ||
    res?.data?.items ||
    res?.data?.partners ||
    res?.data?.rows ||
    res?.partners ||
    res?.rows ||
    res?.data ||
    [];

  return Array.isArray(list) ? list.map(normalizePartner) : [];
}

function ApprovalActionModal({
  open,
  row,
  note,
  setNote,
  saving,
  onClose,
  onApprove,
  onReject,
}) {
  if (!open || !row) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0"
        style={{
          background: "rgba(255,255,255,0.30)",
          backdropFilter: "blur(3px)",
        }}
        onClick={saving ? undefined : onClose}
      />

      <div className="relative z-[1] flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-[500px] rounded-[24px] bg-white p-6 shadow-2xl sm:p-7"
          style={{ border: `1px solid ${BORDER}` }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="absolute right-5 top-5 text-slate-400 transition hover:text-[#d86a78] disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#fff8e8] text-[#b98917]">
              <UserCheck size={24} />
            </div>

            <div>
              <h3 className="text-[22px] font-bold text-slate-700">
                Review Delivery Partner Approval
              </h3>
              <p className="text-[13px] text-slate-500">
                {row.name || "-"} ({row.email || "-"})
              </p>
              <p className="mt-1 text-[12px] font-medium text-[#b98917]">
                Current status: {row.approvalStatus || "PENDING"}
              </p>
            </div>
          </div>

          <div className="mt-5">
            <label className="mb-2 block text-[12px] font-semibold tracking-wide text-slate-500">
              NOTE
            </label>

            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="Add note... (required only for reject)"
              className="w-full rounded-[14px] border border-[#f2c6cc] px-4 py-3 text-[13px] text-slate-700 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
            />
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-[44px] flex-1 rounded-[10px] border border-[#f2c6cc] text-[13px] font-semibold text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={onReject}
              disabled={saving}
              className="h-[44px] flex-1 rounded-[10px] bg-[#d86a78] text-[13px] font-semibold text-white hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "PLEASE WAIT..." : "Reject"}
            </button>

            <button
              type="button"
              onClick={onApprove}
              disabled={saving}
              className="h-[44px] flex-1 rounded-[10px] bg-[#59a647] text-[13px] font-semibold text-white hover:bg-[#4f963f] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "PLEASE WAIT..." : "Approve"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB");
}

function ActionIconButton({
  children,
  title,
  onClick,
  className = "",
  showDot = false,
}) {
  return (
    <div className="relative inline-flex">
      <button
        type="button"
        className={`transition ${className}`}
        title={title}
        onClick={onClick}
      >
        {children}
      </button>

      {showDot ? (
        <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#ef4444] ring-2 ring-white" />
      ) : null}
    </div>
  );
}





function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || "").trim());
}

function isValidPhone(phone) {
  return /^\d{10}$/.test(String(phone || "").trim());
}

function isValidPincode(pincode) {
  return /^\d{6}$/.test(String(pincode || "").trim());
}

function isAlphabeticText(value) {
  return /^[A-Za-z\s.'-]+$/.test(String(value || "").trim());
}

function isAlphaNumericText(value) {
  return /^[A-Za-z0-9\s-]+$/.test(String(value || "").trim());
}

function isValidAddress(value) {
  return /^[A-Za-z0-9\s,./#-]+$/.test(String(value || "").trim());
}

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  avatar: "",
  vehicleType: "",
  vehicleNumber: "",
  pincode: "",
  state: "",
  city: "",
  fullAddress: "",
  hub: "",
  status: "ACTIVE",
  approvalStatus: "PENDING",
};

export default function DeliveryPartners() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const getPositiveNumber = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [partnerCityOptions, setPartnerCityOptions] = useState([]);
  const [partnerCityLoading, setPartnerCityLoading] = useState(false);
  const [showPartnerCityDropdown, setShowPartnerCityDropdown] = useState(false);
  const [errorPopup, setErrorPopup] = useState({
    open: false,
    message: "",
  });

  const [initialForm, setInitialForm] = useState(emptyForm);

  // ✅ yaha likh
  function showError(message) {
    setErrorPopup({ open: true, message });
  }


  const [popup, setPopup] = useState({
    open: false,
    kind: null,
    row: null,
  });

  const [approvalPopup, setApprovalPopup] = useState({
    open: false,
    row: null,
  });
  const [approvalNote, setApprovalNote] = useState("");

  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "");
  const [liveStatusFilter, setLiveStatusFilter] = useState(() => searchParams.get("live") || "");
  const [approvalFilter, setApprovalFilter] = useState(() => searchParams.get("approval") || "");
  const [vehicleFilter, setVehicleFilter] = useState(() => searchParams.get("vehicle") || "");
  const [cityFilter, setCityFilter] = useState(() => searchParams.get("city") || "");
  const [page, setPage] = useState(() =>
    getPositiveNumber(searchParams.get("page"), 1)
  );
  const [pageSize, setPageSize] = useState(() =>
    getPositiveNumber(searchParams.get("limit"), 10)
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(emptyForm);

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    partner: null,
  });

  async function loadPartners({ silent = false } = {}) {
    try {
      if (!silent) setLoading(true);
      const res = await DeliveryPartnersService.list();
      setRows(getRowsFromResponse(res));
    } catch (err) {
      console.error("Failed to load delivery partners:", err);
      showError(err?.message || "Failed to load delivery partners");
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadPartners();
    const intervalId = window.setInterval(() => {
      loadPartners({ silent: true });
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();

    if (page > 1) params.set("page", String(page));
    if (pageSize !== 10) params.set("limit", String(pageSize));
    if (search.trim()) params.set("q", search.trim());
    if (statusFilter) params.set("status", statusFilter);
    if (liveStatusFilter) params.set("live", liveStatusFilter);
    if (approvalFilter) params.set("approval", approvalFilter);
    if (vehicleFilter) params.set("vehicle", vehicleFilter);
    if (cityFilter) params.set("city", cityFilter);

    setSearchParams(params, { replace: true });
  }, [
    page,
    pageSize,
    search,
    statusFilter,
    liveStatusFilter,
    approvalFilter,
    vehicleFilter,
    cityFilter,
    setSearchParams,
  ]);

  useEffect(() => {
    const stateName = String(form.state || "").trim();
    const countryName = "India";

    if (!modalOpen || !stateName) {
      setPartnerCityOptions([]);
      return;
    }

    let active = true;

    (async () => {
      try {
        setPartnerCityLoading(true);

        const rows = await locationService.searchCities({
          countryName,
          stateName,
        });

        if (!active) return;

        const names = rows
          .map((x) => x?.name || x?.city_name || x?.city || "")
          .filter(Boolean);

        setPartnerCityOptions([...new Set(names)]);
      } catch (err) {
        if (active) setPartnerCityOptions([]);
        console.error("Partner city fetch failed", err);
      } finally {
        if (active) setPartnerCityLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [modalOpen, form.state]);

  const filteredPartnerCityOptions = useMemo(() => {
    const q = String(form.city || "").trim().toLowerCase();
    if (!q) return partnerCityOptions.slice(0, 10);

    return partnerCityOptions
      .filter((city) => city.toLowerCase().includes(q))
      .slice(0, 10);
  }, [partnerCityOptions, form.city]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const q = search.trim().toLowerCase();

      const matchSearch =
        !q ||
        [
          row.name,
          row.email,
          row.phone,
          row.vehicleType,
          row.vehicleNumber,
          row.city,
          row.hub,
          row.status,
          row.liveStatus,
          row.partnerId,
          row.approvalStatus,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);

      const matchStatus = !statusFilter || row.status === statusFilter;
      const matchLiveStatus = !liveStatusFilter || row.liveStatus === liveStatusFilter;
      const matchApproval = !approvalFilter || row.approvalStatus === approvalFilter;
      const matchVehicle = !vehicleFilter || row.vehicleType === vehicleFilter;
      const matchCity = !cityFilter || row.city === cityFilter;

      return (
        matchSearch &&
        matchStatus &&
        matchLiveStatus &&
        matchApproval &&
        matchVehicle &&
        matchCity
      );
    });
  }, [rows, search, statusFilter, liveStatusFilter, approvalFilter, vehicleFilter, cityFilter]);

  const paginatedRows = useMemo(() => {
    const total = filteredRows.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(page, pageCount);
    const start = (currentPage - 1) * pageSize;

    return {
      total,
      pageCount,
      currentPage,
      items: filteredRows.slice(start, start + pageSize),
    };
  }, [filteredRows, page, pageSize]);

  useEffect(() => {
    if (!loading && page > paginatedRows.pageCount) {
      setPage(paginatedRows.pageCount);
    }
  }, [loading, page, paginatedRows.pageCount]);

  useEffect(() => {
    if (loading || paginatedRows.total <= 0) return;

    const nextPageSize =
      paginatedRows.total < 10
        ? paginatedRows.total
        : pageSize > paginatedRows.total
          ? paginatedRows.total
          : pageSize < 10
            ? 10
            : pageSize;

    if (nextPageSize !== pageSize) {
      setPage(1);
      setPageSize(nextPageSize);
    }
  }, [loading, paginatedRows.total, pageSize]);

  const pageSizeOptions = useMemo(() => {
    if (paginatedRows.total <= 0) return [10];

    const base = [10, 25, 50, 100, 250, 500];
    const opts = base.filter((n) => n <= paginatedRows.total);

    if (!opts.includes(paginatedRows.total)) opts.push(paginatedRows.total);

    return Array.from(new Set(opts)).sort((a, b) => a - b);
  }, [paginatedRows.total]);

  const onChangePageSize = (value) => {
    const n = Number(value);
    setPage(1);
    setPageSize(Number.isFinite(n) && n > 0 ? n : 10);
  };

  const canPrev = paginatedRows.currentPage > 1;
  const canNext = paginatedRows.currentPage < paginatedRows.pageCount;
  const startIndex =
    paginatedRows.total === 0 ? 0 : (paginatedRows.currentPage - 1) * pageSize + 1;
  const endIndex =
    paginatedRows.total === 0
      ? 0
      : Math.min(paginatedRows.currentPage * pageSize, paginatedRows.total);

  const stats = useMemo(() => {
    return {
      total: rows.length,
      pending: rows.filter((r) => r.approvalStatus === "PENDING").length,
      approved: rows.filter((r) => r.approvalStatus === "APPROVED").length,
      online: rows.filter((r) => r.liveStatus === "AVAILABLE").length,
      busy: rows.filter((r) => ["BUSY", "BUSY_OFFLINE"].includes(r.liveStatus)).length,
      inactive: rows.filter((r) => r.status !== "ACTIVE").length,
    };
  }, [rows]);

  const vehicleOptions = useMemo(() => {
    return [
      ...new Set(
        rows
          .map((r) => r.vehicleType)
          .filter(Boolean)
          .filter((v) => v !== "-")
      ),
    ];
  }, [rows]);

  const cityOptions = useMemo(() => {
    return [
      ...new Set(
        rows
          .map((r) => r.city)
          .filter(Boolean)
          .filter((v) => v !== "-")
      ),
    ];
  }, [rows]);

  const closePopup = () =>
    setPopup({
      open: false,
      kind: null,
      row: null,
    });

  const openTogglePopup = (row) =>
    setPopup({
      open: true,
      kind: row?.status === "ACTIVE" ? "deactivate" : "activate",
      row,
    });

  const closeApprovalPopup = () => {
    setApprovalPopup({
      open: false,
      row: null,
    });
    setApprovalNote("");
  };
  const openApprovalPopup = (row) => {
    setApprovalPopup({
      open: true,
      row,
    });

    setApprovalNote(row?.approvalNote || "");
  };



  function resetModalState() {
    setModalOpen(false);
    setEditingId("");
    setForm(emptyForm);
    setInitialForm(emptyForm);
    setAvatarFile(null);
    setAvatarPreview("");
  }

  function openCreateModal() {
    setModalMode("create");
    setEditingId("");
    setForm(emptyForm);
    setInitialForm(emptyForm);
    setAvatarFile(null);
    setAvatarPreview("");
    setModalOpen(true);
  }

  function openEditModal(row) {
    const nextForm = {
      name: row.name || "",
      email: row.email || "",
      phone: row.phone && row.phone !== "-" ? row.phone : "",
      avatar: row.avatar || "",
      vehicleType: row.vehicleType && row.vehicleType !== "-" ? row.vehicleType : "",
      vehicleNumber:
        row.vehicleNumber && row.vehicleNumber !== "-" ? row.vehicleNumber : "",
      pincode: row.raw?.pincode || "",
      state: row.raw?.state || "",
      city: row.city && row.city !== "-" ? row.city : "",
      fullAddress: row.raw?.full_address || row.raw?.fullAddress || "",
      hub: row.hub && row.hub !== "-" ? row.hub : "",
      status: row.status || "ACTIVE",
      approvalStatus: row.approvalStatus || "PENDING",
    };

    setModalMode("edit");
    setEditingId(row.id);
    setForm(nextForm);
    setInitialForm(nextForm);
    setAvatarFile(null);
    setAvatarPreview(resolveAssetUrl(row.avatar || ""));
    setModalOpen(true);
  }

  function closeModal() {
    resetModalState();
  }

  async function handleFormChange(e) {
    const { name, value } = e.target;

    if (name === "phone") {
      const phone = String(value || "").replace(/\D/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, phone }));
      return;
    }

    if (name === "pincode") {
      const pin = String(value || "").replace(/\D/g, "").slice(0, 6);

      setForm((prev) => ({ ...prev, pincode: pin }));

      if (pin.length === 6) {
        try {
          const rows = await locationService.getPincodeDetails({ pincode: pin });
          const first = Array.isArray(rows) ? rows[0] : null;

          if (first) {
            const city =
              first.city ||
              first.district ||
              first.region ||
              first.taluk ||
              "";

            const state = first.state || "";

            setForm((prev) => ({
              ...prev,
              pincode: pin,
              city: city || prev.city,
              state: state || prev.state,
            }));
          }
        } catch (err) {
          console.error("Partner pincode lookup failed", err);
        }
      }

      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  }



  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.name.trim()) return showError("Full Name is required");
    if (!isAlphabeticText(form.name)) {
      return showError("Full Name should contain only letters and spaces");
    }

    if (!form.email.trim()) return showError("Email is required");
    if (!isValidEmail(form.email)) {
      return showError("Please enter a valid email address");
    }

    if (!form.phone.trim()) return showError("Phone is required");
    if (!isValidPhone(form.phone)) {
      return showError("Phone number must be exactly 10 digits");
    }

    if (!form.vehicleType.trim()) return showError("Vehicle Type is required");
    if (!isAlphabeticText(form.vehicleType)) {
      return showError("Vehicle Type should contain only letters and spaces");
    }

    if (!form.vehicleNumber.trim()) return showError("Vehicle Number is required");
    if (!isAlphaNumericText(form.vehicleNumber)) {
      return showError("Vehicle Number is invalid");
    }

    if (!form.pincode.trim()) return showError("Pincode is required");
    if (!isValidPincode(form.pincode)) {
      return showError("Pincode must be exactly 6 digits");
    }

    if (!form.state.trim()) return showError("State is required");
    if (!isAlphabeticText(form.state)) {
      return showError("State should contain only letters and spaces");
    }

    if (!form.city.trim()) return showError("City is required");
    if (!isAlphabeticText(form.city)) {
      return showError("City should contain only letters and spaces");
    }

    if (!form.fullAddress.trim()) return showError("Full Address is required");
    if (!isValidAddress(form.fullAddress)) {
      return showError("Full Address contains invalid characters");
    }

    if (!form.hub.trim()) return showError("Hub is required");

    if (!avatarFile && !avatarPreview) {
      return showError("Profile image is required");
    }

    try {
      setSaving(true);

      if (modalMode === "create") {
        const payload = {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          vehicleType: form.vehicleType.trim() || null,
          vehicleNumber: form.vehicleNumber.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          pincode: form.pincode.trim() || null,
          fullAddress: form.fullAddress.trim() || null,
          hub: form.hub.trim() || null,
          avatarFile,
        };

        await DeliveryPartnersService.create(payload);
      } else {
        const payload = {
          name: form.name.trim(),
          phone: form.phone.trim() || null,
          vehicleType: form.vehicleType.trim() || null,
          vehicleNumber: form.vehicleNumber.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          pincode: form.pincode.trim() || null,
          fullAddress: form.fullAddress.trim() || null,
          hub: form.hub.trim() || null,
          avatarFile,
        };

        await DeliveryPartnersService.update(editingId, payload);

        const statusChanged =
          String(form.status || "").toUpperCase() !==
          String(initialForm.status || "").toUpperCase();

        if (statusChanged) {
          await DeliveryPartnersService.updateStatus(editingId, {
            status: form.status,
            reason:
              form.status === "ACTIVE"
                ? "Activated by admin"
                : "Deactivated by admin",
          });
        }

        const approvalChanged =
          String(form.approvalStatus || "").toUpperCase() !==
          String(initialForm.approvalStatus || "").toUpperCase();

        if (approvalChanged) {
          showError(
            "Approval status cannot be changed from Edit Partner form. Please use Approve / Reject action."
          );
        }
      }

      resetModalState();
      await loadPartners();
    } catch (err) {
      console.error(err);
      showError(err?.message || "Failed to save delivery partner");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleStatus(row) {
    openTogglePopup(row);
  }

  async function confirmToggleStatus() {
    if (!popup.row?.id || actionLoading) return;

    const nextStatus = popup.row.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    try {
      setActionLoading(true);
      await DeliveryPartnersService.updateStatus(popup.row.id, {
        status: nextStatus,
        reason:
          nextStatus === "ACTIVE"
            ? "Activated by admin"
            : "Deactivated by admin",
      });
      closePopup();
      await loadPartners();
    } catch (err) {
      console.error(err);
      showError(err?.message || "Failed to update status");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApproval(row) {
    openApprovalPopup(row);
  }

  async function submitApprove() {
    if (!approvalPopup.row || savingAction) return;

    try {
      setSavingAction(true);

      await DeliveryPartnersService.updateApproval(approvalPopup.row.id, {
        status: "APPROVED",
        note: approvalNote.trim() || undefined,
      });

      closeApprovalPopup();
      await loadPartners();
    } catch (err) {
      showError(err?.message || "Failed to approve delivery partner");
    } finally {
      setSavingAction(false);
    }
  }

  async function submitReject() {
    if (!approvalPopup.row || savingAction) return;

    if (!approvalNote.trim()) {
      showError("Rejection note is required");
      return;
    }

    try {
      setSavingAction(true);

      await DeliveryPartnersService.updateApproval(approvalPopup.row.id, {
        status: "REJECTED",
        note: approvalNote.trim(),
      });

      closeApprovalPopup();
      await loadPartners();
    } catch (err) {
      showError(err?.message || "Failed to reject delivery partner");
    } finally {
      setSavingAction(false);
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.partner?.id) return;

    try {
      await DeliveryPartnersService.remove(deleteModal.partner.id);
      setDeleteModal({ open: false, partner: null });
      await loadPartners();
    } catch (err) {
      console.error(err);
      showError(err?.message || "Failed to delete delivery partner");
    }
  };

  return (
    <>

      {errorPopup.open && (
        <ConfirmModal
          open={errorPopup.open}
          title="Missing Field"
          description={errorPopup.message}
          onClose={() => setErrorPopup({ open: false, message: "" })}
          onConfirm={() => setErrorPopup({ open: false, message: "" })}
          confirmText="OK"
        />
      )}
      {deleteModal.open && (
        <ConfirmModal
          open={deleteModal.open}
          title={`Are you sure you want to Delete ${deleteModal.partner?.name}?`}
          description="This will permanently remove the delivery partner record from the list."
          onClose={() => setDeleteModal({ open: false, partner: null })}
          onConfirm={handleDeleteConfirm}
        />
      )}
      <ConfirmModal
        open={popup.open && (popup.kind === "activate" || popup.kind === "deactivate")}
        icon="ban"
        variant="confirm"
        title={
          popup.kind === "activate"
            ? `Are you sure you want to Activate ${popup.row?.name || ""}?`
            : `Are you sure you want to Deactivate ${popup.row?.name || ""}?`
        }
        description={
          popup.kind === "activate"
            ? "This delivery partner will be able to sign in again and use the panel."
            : "This delivery partner will not be able to sign in until activated again."
        }
        onClose={closePopup}
        onConfirm={confirmToggleStatus}
        cancelText="NO"
        confirmText={actionLoading ? "PLEASE WAIT..." : "YES"}
      />

      <ApprovalActionModal
        open={approvalPopup.open}
        row={approvalPopup.row}
        note={approvalNote}
        setNote={setApprovalNote}
        saving={savingAction}
        onClose={closeApprovalPopup}
        onApprove={submitApprove}
        onReject={submitReject}
      />

      <div className="">
        <div className="w-full">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">
                DELIVERY PARTNER DETAILS
              </h1>
            </div>

            <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:justify-end">
              <button
                type="button"
                className="inline-flex h-[44px] w-full sm:w-auto items-center justify-center rounded-full px-5 sm:px-6 text-[14px] sm:text-[15px] font-semibold text-white transition"
                style={{ backgroundColor: PINK }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = PINK_HOVER)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = PINK)
                }
                onClick={openCreateModal}
              >
                <Plus size={16} className="mr-2" />
                Add New Partner
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
            <StatCard title="Total Partners" value={stats.total} tone="pink" />
            <StatCard
              title="Pending Approvals"
              value={stats.pending}
              tone="yellow"
            />
            <StatCard title="Approved" value={stats.approved} tone="green" />
            <StatCard title="Online & Available" value={stats.online} tone="green" />
            <StatCard title="Busy" value={stats.busy} tone="yellow" />
            <StatCard title="Inactive Accounts" value={stats.inactive} tone="red" />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(220px,1.6fr)_repeat(5,minmax(145px,1fr))_auto]">
            <div className="relative sm:col-span-2 xl:col-span-1">
              <Search
                size={16}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search partner"
                className="h-[44px] w-full rounded-[12px] bg-white pl-10 pr-4 text-[14px] text-slate-600 outline-none"
                style={{ border: `1px solid ${BORDER}` }}
              />
            </div>

            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </Select>

            <Select
              value={liveStatusFilter}
              onChange={(e) => {
                setLiveStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Live Status</option>
              <option value="AVAILABLE">Online</option>
              <option value="BUSY">Busy & Online</option>
              <option value="BUSY_OFFLINE">Busy & Offline</option>
              <option value="PAUSED">Paused</option>
              <option value="OFFLINE">Offline</option>
            </Select>

            <Select
              value={approvalFilter}
              onChange={(e) => {
                setApprovalFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Approval</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
            </Select>

            <Select
              value={vehicleFilter}
              onChange={(e) => {
                setVehicleFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Vehicle Type</option>
              {vehicleOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </Select>

            <Select
              value={cityFilter}
              onChange={(e) => {
                setCityFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">City</option>
              {cityOptions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </Select>

            <div className="flex items-center justify-start xl:justify-end">
              <div
                className="inline-flex min-h-[44px] items-center rounded-[12px] px-4 text-[14px] font-semibold"
                style={{ border: `1px solid ${BORDER}`, color: PINK }}
              >
                {filteredRows.length} Visible Partners
              </div>
            </div>
          </div>

          <Card className="mt-5 overflow-hidden">
            <div className="hidden lg:block w-full overflow-x-auto">
              <div className="max-h-[520px] min-w-[1180px] overflow-y-auto">
                <table className="w-full border-separate border-spacing-0">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr className="bg-white">
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Account Status
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Live Status
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Driver ID
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Approval
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Name
                      </th>
                      <th
                        className="min-w-[190px] border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Email
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Phone
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Vehicle Type
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Vehicle Number
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        City
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Hub
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Joined
                      </th>
                      <th
                        className="border-b px-3 py-4 md:px-4 md:py-4 text-left text-[13px] md:text-[14px] font-bold text-[#d86a78]"
                        style={{ borderColor: "#f1f5f9" }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={13}
                          className="px-5 py-10 text-center text-[15px] text-slate-400"
                        >
                          Loading delivery partners...
                        </td>
                      </tr>
                    ) : filteredRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={13}
                          className="px-5 py-10 text-center text-[15px] text-slate-400"
                        >
                          No delivery partners found
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.items.map((row) => (
                        <tr key={row.id || `${row.email}-${row.phone}`} className="bg-white">
                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            <Badge type={row.status === "ACTIVE" ? "active" : "inactive"}>
                              {row.status === "ACTIVE" ? "Active" : "Inactive"}
                            </Badge>
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            <LiveStatusPill status={row.liveStatus} activeTaskCount={row.activeTaskCount} />
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4 text-[12px] text-slate-500 break-all"
                            style={{ borderColor: "#f1f5f9" }}
                            title={row.id}
                          >
                            {row.partnerId || row.id || "-"}
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            <ApprovalPill value={row.approvalStatus} />
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4 text-[14px] break-words text-slate-600 cursor-pointer"
                            onClick={() => navigate(`/admin/dashboard/delivery-partners/${row.id}`)}
                          >
                            {row.name}
                          </td>

                          <td
                            className="min-w-[190px] border-b px-3 py-4 md:px-4 md:py-4 text-[14px] text-slate-400"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            <div
                              className="max-w-[210px] truncate whitespace-nowrap"
                              title={row.email || ""}
                            >
                              {row.email || "-"}
                            </div>
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4 text-[14px] text-slate-400 break-words"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            {row.phone}
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4 text-[14px] text-slate-400 break-words"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            {row.vehicleType}
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4 text-[14px] text-slate-400 break-words"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            {row.vehicleNumber}
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4 text-[14px] text-slate-400 break-words"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            {row.city}
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4 text-[14px] text-slate-400 break-words"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            {row.hub}
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4 text-[14px] text-slate-400 break-words"
                            style={{ borderColor: "#f1f5f9" }}
                          >
                            {formatDate(row.joinedAt)}
                          </td>

                          <td
                            className="border-b px-3 py-4 md:px-4 md:py-4"
                            style={{ borderColor: "#f1f5f9" }}
                          >

                            {/* <button
  type="button"
  className="transition hover:text-[#d86a78]"
  title="View Profile"
  onClick={() => navigate(`/admin/dashboard/delivery-partners/${row.id}`)}
>
  <Eye size={18} />
</button> */}
                            <div className="flex flex-wrap items-center gap-2 text-slate-600">
                              <button
                                type="button"
                                className="transition hover:text-[#d86a78]"
                                title="Edit"
                                onClick={() => openEditModal(row)}
                              >
                                <Pencil size={18} />
                              </button>

                              {(() => {
                                const isPending =
                                  String(row.approvalStatus || "").toUpperCase() === "PENDING";

                                return (
                                  <ActionIconButton
                                    title="Review Approval"
                                    className="hover:text-[#b98917]"
                                    onClick={() => handleApproval(row)}
                                    showDot={isPending}
                                  >
                                    <UserCheck
                                      size={18}
                                      className={isPending ? "text-[#b98917]" : "text-slate-500"}
                                    />
                                  </ActionIconButton>
                                );
                              })()}

                              <button
                                type="button"
                                className={`transition ${row.status === "ACTIVE"
                                  ? "text-[#69a65f] hover:text-[#4d8a44]"
                                  : "text-slate-500 hover:text-[#d86a78]"
                                  }`}
                                title={row.status === "ACTIVE" ? "Deactivate" : "Activate"}
                                onClick={() => handleToggleStatus(row)}
                              >
                                <Power size={18} />
                              </button>

                              <button
                                type="button"
                                className="transition hover:text-[#d86a78]"
                                title="Delete"
                                onClick={() => setDeleteModal({ open: true, partner: row })}
                              >
                                <Trash2 size={16} className="text-red-500 cursor-pointer" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 lg:hidden">
              {loading ? (
                <Card className="p-5 text-center text-[15px] text-slate-400">
                  Loading delivery partners...
                </Card>
              ) : filteredRows.length === 0 ? (
                <Card className="p-5 text-center text-[15px] text-slate-400">
                  No delivery partners found
                </Card>
              ) : (
                paginatedRows.items.map((row) => (
                  <Card key={row.id || `${row.email}-${row.phone}`} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div
                          className="text-[15px] font-semibold text-[#d86a78] break-words cursor-pointer hover:underline"
                          onClick={() => navigate(`/admin/dashboard/delivery-partners/${row.id}`)}
                        >
                          {row.name}
                        </div>
                        <div className="mt-1 text-[13px] text-slate-400 break-all">
                          {row.email}
                        </div>
                      </div>

                      <div className="flex shrink-0 flex-col items-end gap-2">
                        <LiveStatusPill status={row.liveStatus} activeTaskCount={row.activeTaskCount} />
                        <ApprovalPill value={row.approvalStatus} />
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-[13px]">
                      <div>
                        <div className="text-slate-400">Driver ID</div>
                        <div className="mt-1 break-all text-slate-600">{row.partnerId || row.id || "-"}</div>
                      </div>
                      <div>
                        <div className="text-slate-400">Account</div>
                        <div className="mt-1 text-slate-600">{row.status === "ACTIVE" ? "Active" : "Inactive"}</div>
                      </div>
                      <div>
                        <div className="text-slate-400">Phone</div>
                        <div className="mt-1 break-words text-slate-600">{row.phone}</div>
                      </div>
                      <div>
                        <div className="text-slate-400">Vehicle</div>
                        <div className="mt-1 break-words text-slate-600">{row.vehicleType}</div>
                      </div>
                      <div>
                        <div className="text-slate-400">Vehicle No.</div>
                        <div className="mt-1 break-words text-slate-600">{row.vehicleNumber}</div>
                      </div>
                      <div>
                        <div className="text-slate-400">City</div>
                        <div className="mt-1 break-words text-slate-600">{row.city}</div>
                      </div>
                      {/* <div>
                        <div className="text-slate-400">Hub</div>
                        <div className="mt-1 break-words text-slate-600">{row.hub}</div>
                      </div> */}
                      <div>
                        <div className="text-slate-400">Joined</div>
                        <div className="mt-1 break-words text-slate-600">{formatDate(row.joinedAt)}</div>
                      </div>
                    </div>

                    {/* <button
  type="button"
  className="rounded-full border px-3 py-2 text-[12px] font-medium transition hover:text-[#d86a78]"
  style={{ borderColor: BORDER }}
  onClick={() => navigate(`/admin/dashboard/delivery-partners/${row.id}`)}
>
  <span className="inline-flex items-center gap-1">
    <Eye size={14} />
    View
  </span>
</button> */}

                    <div className="mt-4 flex flex-wrap items-center gap-2 text-slate-600">
                      <button
                        type="button"
                        className="rounded-full border px-3 py-2 text-[12px] font-medium transition hover:text-[#d86a78]"
                        style={{ borderColor: BORDER }}
                        onClick={() => openEditModal(row)}
                      >
                        <span className="inline-flex items-center gap-1">
                          <Pencil size={14} />
                          Edit
                        </span>
                      </button>

                      {(() => {
                        const isPending =
                          String(row.approvalStatus || "").toUpperCase() === "PENDING";

                        return (
                          <div className="relative inline-flex">
                            <button
                              type="button"
                              className="rounded-full border px-3 py-2 text-[12px] font-medium text-[#b98917]"
                              style={{ borderColor: BORDER }}
                              onClick={() => handleApproval(row)}
                            >
                              <span className="inline-flex items-center gap-1">
                                <UserCheck size={14} />
                                Review
                              </span>
                            </button>

                            {isPending ? (
                              <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#ef4444] ring-2 ring-white" />
                            ) : null}
                          </div>
                        );
                      })()}

                      <button
                        type="button"
                        className="rounded-full border px-3 py-2 text-[12px] font-medium"
                        style={{ borderColor: BORDER }}
                        onClick={() => handleToggleStatus(row)}
                      >
                        <span className="inline-flex items-center gap-1">
                          <Power size={14} />
                          {row.status === "ACTIVE" ? "Deactivate" : "Activate"}
                        </span>
                      </button>

                      <button
                        type="button"
                        className="rounded-full border px-3 py-2 text-[12px] font-medium text-red-500"
                        style={{ borderColor: BORDER }}
                        onClick={() => setDeleteModal({ open: true, partner: row })}
                      >
                        <span className="inline-flex items-center gap-1">
                          <Trash2 size={14} />
                          Delete
                        </span>
                      </button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </Card>

          <div className="mt-5 flex flex-col gap-3 rounded-[18px] border border-[#f2c6cc] bg-white px-4 py-4 text-[12px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-center sm:text-left">
              Showing {startIndex}-{endIndex} of {paginatedRows.total}
            </div>

            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center justify-between gap-3 sm:justify-start">
                <span>Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => onChangePageSize(e.target.value)}
                  className="h-9 min-w-[88px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78]"
                >
                  {pageSizeOptions.map((n) => (
                    <option key={n} value={n}>
                      {n === paginatedRows.total && paginatedRows.total > 10
                        ? `All (${paginatedRows.total})`
                        : n}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between gap-2 sm:justify-start">
                <button
                  type="button"
                  onClick={() => canPrev && setPage((p) => p - 1)}
                  disabled={!canPrev}
                  className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
                >
                  Prev
                </button>

                <span className="min-w-[86px] text-center">
                  Page {paginatedRows.currentPage} / {paginatedRows.pageCount}
                </span>

                <button
                  type="button"
                  onClick={() => canNext && setPage((p) => p + 1)}
                  disabled={!canNext}
                  className="rounded-[10px] border border-[#f2c6cc] px-4 py-2 font-semibold text-slate-600 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <DeliveryPartnerModal
          open={modalOpen}
          mode={modalMode}
          form={form}
          onChange={handleFormChange}
          onClose={closeModal}
          onSubmit={handleSubmit}
          saving={saving}
          avatarFile={avatarFile}
          avatarPreview={avatarPreview}
          onAvatarChange={handleAvatarChange}
          cityOptions={partnerCityOptions}
          cityLoading={partnerCityLoading}
          showCityDropdown={showPartnerCityDropdown}
          setShowCityDropdown={setShowPartnerCityDropdown}
          filteredCityOptions={filteredPartnerCityOptions}
        />
      </div>
    </>
  );
}